/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import axiosInstance from "../api/axiosInstance";
import useDebounce from "../hooks/useDebounce";
import { useAuth } from "../context/AuthContext";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

import { Alert, Button, Input, Table, Tooltip, Typography } from "antd";
import { CardTitle, ListToolbar, PageHeader, SectionCard, StatusTag, fmtDateTime, tablePagination, useFlatPage } from "../components/ui";
import { IconDownload } from "../components/ui/icons";
import notify from "../utils/notify";

const { Text } = Typography;

const SystemLogPage = () => {
  useFlatPage();
  const { authData } = useAuth();

  /* --------- access scope --------- */
  const roleStr = String(authData?.role || "").toLowerCase();
  const canViewAllHotels =
    roleStr === "systemadmin" ||
    (Array.isArray(authData?.permissions) &&
      authData.permissions.includes("Logs.ViewAll"));

  /* ---------------- state ---------------- */
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  // filters
  const [eventType, setEventType] = useState("");
  const [userEmail, setUserEmail] = useState(""); // ✅ email search (not userId)

  // Hotel filter only if allowed
  const [hotelId, setHotelId] = useState(
    canViewAllHotels ? "" : authData?.hotelId ?? ""
  );

  // debounce
  const debouncedType = useDebounce(eventType, 300);
  const debouncedEmail = useDebounce(userEmail, 300);
  const debouncedHotelId = useDebounce(hotelId, 300);

  /* keep hotelId in sync if auth changes and user cannot view all */
  useEffect(() => {
    if (!canViewAllHotels) {
      setHotelId(authData?.hotelId ?? "");
    }
  }, [authData?.hotelId, canViewAllHotels]);

  /* ---------------- effects ---------------- */
  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setErrorMsg(null);

    // require a hotelId when user is restricted
    if (!canViewAllHotels && !authData?.hotelId) {
      setLoading(false);
      setErrorMsg("Missing hotel scope. Please re-login or contact admin.");
      return;
    }

    try {
      const params = {
        // If your API expects `userEmail` instead of `email`, rename this key.
        email: debouncedEmail || undefined,
        eventType: debouncedType || undefined,
      };

      if (canViewAllHotels) {
        if (debouncedHotelId) params.hotelId = debouncedHotelId;
      } else {
        params.hotelId = authData?.hotelId;
      }

      const res = await axiosInstance.get("/auth/system-logs", { params });
      setLogs(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error("Log fetch failed", e);
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        "Failed to load logs. Please try again.";
      setErrorMsg(msg);
      notify.error(msg);
    } finally {
      setLoading(false);
    }
  }, [
    debouncedType,
    debouncedEmail,
    debouncedHotelId,
    canViewAllHotels,
    authData?.hotelId,
  ]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  /* ---------------- client-side email filter (fallback / contains) ---------------- */
  const displayedLogs = useMemo(() => {
    if (!userEmail) return logs;
    const q = userEmail.toLowerCase();
    return logs.filter((l) => String(l.email || "").toLowerCase().includes(q));
  }, [logs, userEmail]);

  /* ---------------- export ---------------- */
  const exportCsv = () => {
    if (!displayedLogs.length) {
      notify.info("No logs to export.");
      return;
    }
    const ws = XLSX.utils.json_to_sheet(displayedLogs);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Logs");
    const buf = XLSX.write(wb, { type: "array", bookType: "csv" });
    saveAs(
      new Blob([buf], { type: "text/csv;charset=utf-8;" }),
      `system_logs_${Date.now()}.csv`
    );
    notify.success("Exported CSV");
  };

  /* ---------------- helpers ---------------- */
  // Event type -> status chip
  const typeStatus = (t = "") => {
    const key = t.toLowerCase();
    if (key.includes("fail") || key.includes("error")) return "cancelled";
    if (key.includes("delete") || key.includes("remove")) return "noshow";
    if (key.includes("update") || key.includes("edit")) return "pending";
    if (key.includes("logout")) return "confirmed";
    if (key.includes("login") || key.includes("created")) return "available";
    return "";
  };

  const uniqueEventTypes = useMemo(
    () =>
      Array.from(new Set(logs.map((l) => l?.eventType).filter(Boolean))).sort(),
    [logs]
  );

  /* ---------------- table ---------------- */
  const columns = [
    {
      title: "Time",
      dataIndex: "eventTime",
      key: "eventTime",
      width: 200,
      sorter: (a, b) => new Date(a.eventTime) - new Date(b.eventTime),
      defaultSortOrder: "descend",
      render: (_, r) => (
        <Tooltip title={`UTC: ${new Date(r.eventTime).toISOString()}`}>
          <span>{fmtDateTime(r.eventTime)}</span>
        </Tooltip>
      ),
    },
    {
      title: "Hotel",
      dataIndex: "hotelId",
      key: "hotelId",
      width: 90,
      align: "right",
      render: (v) => v ?? "—",
      sorter: (a, b) => (a.hotelId ?? 0) - (b.hotelId ?? 0),
    },
    {
      title: "User",
      dataIndex: "userId",
      key: "userId",
      width: 90,
      align: "right",
      render: (v) => v ?? "—",
      sorter: (a, b) => (a.userId ?? 0) - (b.userId ?? 0),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      width: 300,
      render: (email) =>
        email ? (
          <span>
            <a href={`mailto:${email}`}>{email}</a> <Text copyable={{ text: email }} />
          </span>
        ) : (
          <span className="hms-muted">—</span>
        ),
      sorter: (a, b) => String(a.email || "").localeCompare(String(b.email || "")),
    },
    {
      title: "Event",
      dataIndex: "eventType",
      key: "eventType",
      width: 190,
      filters: uniqueEventTypes.map((et) => ({ text: et, value: et })),
      onFilter: (value, record) => (record.eventType || "") === value,
      render: (t) => (t ? <StatusTag status={typeStatus(t)}>{t}</StatusTag> : <span className="hms-muted">—</span>),
      sorter: (a, b) => String(a.eventType || "").localeCompare(String(b.eventType || "")),
    },
    {
      title: "IP address",
      dataIndex: "ipAddress",
      key: "ipAddress",
      width: 150,
      render: (ip) => (ip ? <Text copyable>{ip}</Text> : <span className="hms-muted">—</span>),
      sorter: (a, b) => String(a.ipAddress || "").localeCompare(String(b.ipAddress || "")),
    },
    {
      title: "User agent",
      dataIndex: "userAgent",
      key: "userAgent",
      ellipsis: true,
      render: (ua) =>
        ua ? (
          <Tooltip title={ua}>
            <span>{ua}</span>
          </Tooltip>
        ) : (
          <span className="hms-muted">—</span>
        ),
    },
  ];

  /* ---------------- render ---------------- */
  return (
    <div className="hms-page log-page">
      <PageHeader
        actions={
          <Button icon={<IconDownload />} onClick={exportCsv}>
            Export CSV
          </Button>
        }
      />

      {errorMsg && (
        <Alert style={{ marginBottom: 12 }} type="error" title="Error loading logs" description={errorMsg} showIcon />
      )}

      <SectionCard
        flush
        title={
          <>
            <CardTitle count={displayedLogs.length}>System logs</CardTitle>
            {!canViewAllHotels && (
              <span style={{ marginLeft: 10 }}>
                <StatusTag status="confirmed">Your hotel only</StatusTag>
              </span>
            )}
          </>
        }
        extra={
          <ListToolbar
            search={{ value: userEmail, onChange: setUserEmail, placeholder: "Search email" }}
            onRefresh={fetchLogs}
            loading={loading}
          >
            {canViewAllHotels && (
              <Input
                type="number"
                inputMode="numeric"
                style={{ width: 150 }}
                placeholder="Hotel ID"
                value={hotelId}
                onChange={(e) => setHotelId(e.target.value)}
                allowClear
              />
            )}
            <Input
              style={{ width: 200 }}
              placeholder="Event type"
              value={eventType}
              onChange={(e) => setEventType(e.target.value)}
              allowClear
              list="event-types"
            />
            <datalist id="event-types">
              {uniqueEventTypes.map((et) => (
                <option key={et} value={et} />
              ))}
            </datalist>
          </ListToolbar>
        }
      >
        <Table
          className="hms-table"
          rowKey="systemLogId"
          columns={columns}
          dataSource={displayedLogs}
          loading={loading}
          sticky
          pagination={tablePagination("log", { pageSize: 25, pageSizeOptions: [10, 25, 50, 100] })}
          scroll={{ x: 1100 }}
          locale={{ emptyText: <div className="hms-empty">No logs found</div> }}
        />
      </SectionCard>
    </div>
  );
};

export default SystemLogPage;
